import {
  DefaultTool,
  EdgelessLegacySlotIdentifier,
} from '@blocksuite/affine-block-surface';
import { toast } from '@blocksuite/affine-components/toast';
import { PanTool } from '@blocksuite/affine-gfx-pointer';
import type { FrameBlockModel } from '@blocksuite/affine-model';
import {
  edgelessToolbarI18nT,
  EditPropsStore,
  ViewportElementProvider,
} from '@blocksuite/affine-shared/services';
import { EdgelessToolbarToolMixin } from '@blocksuite/affine-widget-edgeless-toolbar';
import { Bound } from '@blocksuite/global/gfx';
import { SignalWatcher } from '@blocksuite/global/lit';
import {
  EndPointArrowIcon,
  ExpandCloseIcon,
  ExpandFullIcon,
  StartPointArrowIcon,
  StopAiIcon,
} from '@blocksuite/icons/lit';
import type { BlockComponent } from '@blocksuite/std';
import type { ToolOptions } from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, html, LitElement, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  cinematicNavigateToFrame,
  cinematicNavigateToViewport,
  type ViewportNavigationTarget,
} from '../cinematic-frame-navigation.js';
import {
  EdgelessFrameManagerIdentifier,
  isFrameBlock,
  type NavigatorMode,
} from '../frame-manager';
import { PresentTool } from '../present-tool';

/**
 * PresentationToolbar - sl-2vda SOLID Refactoring
 *
 * This toolbar is now "DUMB" - it has NO internal frame index state.
 *
 * SOLID Principles:
 * - Single Responsibility: UI only, no state management
 * - Open/Closed: Reacts to external slots, doesn't decide state
 * - Dependency Inversion: Depends on slots (abstractions), not internal state
 *
 * Data Flow:
 * - INPUT: frameIndexChanged slot → toolbar displays frame
 * - OUTPUT: userRequestedFrameChange slot → service handles navigation
 *
 * The PresentationFrameStateService is the Single Source of Truth.
 */
export class PresentationToolbar extends EdgelessToolbarToolMixin(
  SignalWatcher(LitElement)
) {
  static override styles = css`
    :host {
      align-items: inherit;
      width: 100%;
      height: 100%;
      gap: 8px;
      padding-right: 2px;
    }
    .full-divider {
      width: 8px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .full-divider::after {
      content: '';
      width: 1px;
      height: 100%;
      background: var(--streamlain-v2-layer-insideBorder-border);
      transform: scaleX(0.5);
    }
    .config-buttons {
      display: flex;
      gap: 10px;
    }
    .edgeless-frame-navigator {
      width: 140px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .edgeless-frame-navigator.dense {
      width: auto;
    }

    .edgeless-frame-navigator-title {
      display: inline-block;
      cursor: pointer;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      padding-right: 8px;
    }

    .edgeless-frame-navigator-count {
      color: var(--streamlain-v2-text-secondary);
      white-space: nowrap;
    }
    .edgeless-frame-navigator-stop {
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 8px;
      position: relative;
      overflow: hidden;

      svg {
        display: block;
        width: 24px;
        height: 24px;
        color: white;
      }
    }
    .edgeless-frame-navigator-stop::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: transparent;
      border-radius: inherit;
    }
    .edgeless-frame-navigator-stop:hover::before {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
    }
  `;

  private _prePresentationViewport: ViewportNavigationTarget | null = null;

  private _entryCameraFlight = false;

  private _exitCameraFlight: Promise<void> | null = null;

  /**
   * Whether this is a follower in collaborative presentation mode.
   * In follower mode:
   * - Navigation is controlled externally via frameIndexChanged slot
   * - Keyboard shortcuts (except ESC) are disabled
   * - UI shows only frame counter, no navigation controls
   */
  private _isFollowerMode = false;

  /**
   * sl-2vda SOLID: Frame index from EXTERNAL source (Service via slot)
   * This is NOT internal state - it's a cached copy of the external truth.
   * Updated ONLY via frameIndexChanged slot subscription.
   */
  private _displayedFrameIndex = 0;

  /**
   * Animation enabled flag - set after initial frame is shown
   */
  private _isAnimationEnabled = false;

  override type = PresentTool;

  private get _frames(): FrameBlockModel[] {
    return this.edgeless.std.get(EdgelessFrameManagerIdentifier).frames;
  }

  get dense() {
    return this.containerWidth < 554;
  }

  get host() {
    return this.edgeless.host;
  }

  get slots() {
    return this.edgeless.std.get(EdgelessLegacySlotIdentifier);
  }

  constructor(edgeless: BlockComponent) {
    super();
    this.edgeless = edgeless;
  }

  private _bindHotKey() {
    const handleKeyIfFrameNavigator = (action: () => void) => () => {
      if (this.edgelessTool.toolType === PresentTool) {
        // In follower mode, only ESC is allowed (to exit)
        // Navigation keys are disabled - frame is controlled externally
        if (this._isFollowerMode && action !== this._exitPresentation) {
          return;
        }
        action();
      }
    };

    this.edgeless.bindHotKey(
      {
        ArrowLeft: handleKeyIfFrameNavigator(() =>
          this._requestPreviousFrame()
        ),
        ArrowRight: handleKeyIfFrameNavigator(() => this._requestNextFrame()),
        Escape: handleKeyIfFrameNavigator(() => this._exitPresentation()),
      },
      {
        global: true,
      }
    );
  }

  private _exitPresentation() {
    if (document.fullscreenElement) {
      this._exitPresentationAfterFullscreen = true;
      document.exitFullscreen().catch(console.error);
    } else {
      void this._restorePrePresentationViewport();
    }

    // Reset the flag when fully exiting presentation mode
    this.edgeless.std
      .get(EditPropsStore)
      .setStorage('presentNoFrameToastShown', false);
  }

  private async _restorePrePresentationViewport() {
    if (this._exitCameraFlight) {
      await this._exitCameraFlight;
      return;
    }

    const restore = async () => {
      const target = this._prePresentationViewport;
      if (target) {
        await cinematicNavigateToViewport(this.gfx.viewport, target, {
          style: 'smooth',
        });
      }
      this._prePresentationViewport = null;

      if (this.edgeless.store.readonly) {
        this.setEdgelessTool(PanTool, { panning: false });
      } else {
        this.setEdgelessTool(DefaultTool);
      }
    };

    this._exitCameraFlight = restore().finally(() => {
      this._exitCameraFlight = null;
    });
    await this._exitCameraFlight;
  }

  private _enterPresentation() {
    if (this._entryCameraFlight || document.fullscreenElement) return;

    const viewport = this.gfx.viewport;
    this._prePresentationViewport ??= {
      centerX: viewport.centerX,
      centerY: viewport.centerY,
      zoom: viewport.zoom,
    };
    this._entryCameraFlight = true;

    // Fullscreen must be requested synchronously from the originating click or
    // the browser may reject it after transient user activation expires. The
    // fullscreenchange handler starts the camera flight once the presentation
    // viewport has its final dimensions.
    this._toggleFullScreen();
  }

  /**
   * Navigate viewport to the currently displayed frame
   */
  private _moveToFrame(frameIndex: number, animate = true) {
    const currentToolOption = this.gfx.tool.currentToolOption$.value;
    const toolOptions = currentToolOption?.options;

    // If PresentTool is being activated after a temporary pan (indicated by restoredAfterPan)
    // skip moving to preserve the user's panned position.
    if (
      currentToolOption?.toolType === PresentTool &&
      toolOptions?.restoredAfterPan
    ) {
      // Clear the flag so future navigations behave normally
      const currentTool = this.gfx.tool.currentTool$.peek();
      if (currentTool?.activatedOption) {
        currentTool.activatedOption.restoredAfterPan = false;
      }
      return;
    }

    const viewport = this.gfx.viewport;
    const frame = this._frames[frameIndex];

    if (frame) {
      // Use smooth animation for frame transitions
      if (animate && this._isAnimationEnabled) {
        cinematicNavigateToFrame(viewport, frame, {
          mode: this._navigatorMode,
          style: 'smooth',
        })
          .then(() => {
            this.slots.navigatorFrameChanged.next(frame);
          })
          .catch(console.error);
      } else {
        // Instant transition (for initial load or when animation is disabled)
        let bound = Bound.deserialize(frame.xywh);

        if (this._navigatorMode === 'fill') {
          const vb = viewport.viewportBounds;
          const center = bound.center;
          let w, h;
          if (vb.w / vb.h > bound.w / bound.h) {
            w = bound.w;
            h = (w * vb.h) / vb.w;
          } else {
            h = bound.h;
            w = (h * vb.w) / vb.h;
          }
          bound = Bound.fromCenter(center, w, h);
        }

        viewport.setViewportByBound(bound, [0, 0, 0, 0], false);
        this.slots.navigatorFrameChanged.next(frame);
      }
    }
  }

  /**
   * sl-2vda SOLID: Request next frame via slot (OUTPUT)
   * Does NOT change internal state - emits event for service to handle.
   */
  private _requestNextFrame() {
    const frames = this._frames;
    const max = frames.length - 1;

    if (this._displayedFrameIndex >= max) {
      toast(this.host, 'You have reached the last frame');
      return;
    }

    // Emit request to service - service will respond via frameIndexChanged
    this.slots.userRequestedFrameChange.next(this._displayedFrameIndex + 1);
  }

  /**
   * sl-2vda SOLID: Request previous frame via slot (OUTPUT)
   * Does NOT change internal state - emits event for service to handle.
   */
  private _requestPreviousFrame() {
    if (this._displayedFrameIndex <= 0) {
      toast(this.host, 'You have reached the first frame');
      return;
    }

    // Emit request to service - service will respond via frameIndexChanged
    this.slots.userRequestedFrameChange.next(this._displayedFrameIndex - 1);
  }

  /**
   * Toggle fullscreen, but keep edgeless tool to frameNavigator
   */
  private _toggleFullScreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
      this._fullScreenMode = false;
    } else {
      const { viewportElement } = this.edgeless.std.get(
        ViewportElementProvider
      );
      launchIntoFullscreen(viewportElement);
      this._fullScreenMode = true;
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();

    const { _disposables } = this;

    // Track tool activation and follower mode
    _disposables.add(
      effect(() => {
        const currentToolOption = this.gfx.tool.currentToolOption$.value;

        if (currentToolOption?.toolType === PresentTool) {
          const opts = currentToolOption.options as
            | ToolOptions<PresentTool>
            | undefined;

          // Track follower mode
          this._isFollowerMode = !!opts?.followerMode;

          const isAlreadyFullscreen = !!document.fullscreenElement;

          // Frame presentation and browser fullscreen are independent. The
          // moderator enters fullscreen only through the toolbar button.
          this._fullScreenMode = isAlreadyFullscreen;
          this._isAnimationEnabled = true;

          this._navigatorMode = opts?.mode ?? this._navigatorMode;

          // Check for selected frame
          const selection = this.gfx.selection;
          if (
            selection.selectedElements.length > 0 &&
            isFrameBlock(selection.selectedElements[0])
          ) {
            const selectedFrameId = selection.selectedElements[0].id;
            const indexOfSelectedFrame = this._frames.findIndex(
              frame => frame.id === selectedFrameId
            );
            if (indexOfSelectedFrame !== -1) {
              // Request navigation to selected frame
              this.slots.userRequestedFrameChange.next(indexOfSelectedFrame);
            }
          }

          const store = this.edgeless.std.get(EditPropsStore);
          if (this._frames.length === 0) {
            if (!store.getStorage('presentNoFrameToastShown')) {
              toast(
                this.host,
                'The presentation requires at least 1 frame. You can firstly create a frame.',
                5000
              );
              store.setStorage('presentNoFrameToastShown', true);
            }
          } else {
            if (store.getStorage('presentNoFrameToastShown')) {
              store.setStorage('presentNoFrameToastShown', false);
            }
          }
        } else {
          // Exiting PresentTool
          this._isFollowerMode = false;

          if (document.fullscreenElement && this._fullScreenMode) {
            document.exitFullscreen().catch(console.error);
            this._fullScreenMode = false;
          }
        }
        this.requestUpdate();
      })
    );

    // sl-2vda SOLID: Subscribe to frameIndexChanged (INPUT from Service)
    // This is the ONLY way the toolbar gets its frame index
    _disposables.add(
      this.slots.frameIndexChanged.subscribe((frameIndex: number) => {
        console.log(
          '[PresentationToolbar] frameIndexChanged received:',
          frameIndex
        );

        const frames = this._frames;
        if (frameIndex >= 0 && frameIndex < frames.length) {
          const previousIndex = this._displayedFrameIndex;
          this._displayedFrameIndex = frameIndex;

          // Navigate to the frame
          const shouldAnimate =
            this._isAnimationEnabled && previousIndex !== frameIndex;
          if (!this._entryCameraFlight) {
            this._moveToFrame(frameIndex, shouldAnimate);
          }

          // Trigger re-render
          this.requestUpdate();
        }
      })
    );

    // Legacy support: navigatorSetFrame for follower mode (deprecated)
    _disposables.add(
      this.slots.navigatorSetFrame.subscribe((frameIndex: number) => {
        if (!this._isFollowerMode) return;

        // Enable animations for followers
        if (!this._isAnimationEnabled) {
          this._isAnimationEnabled = true;
        }

        const frames = this._frames;
        if (frameIndex >= 0 && frameIndex < frames.length) {
          this._displayedFrameIndex = frameIndex;
          this._moveToFrame(frameIndex, true);
          this.requestUpdate();
        }
      })
    );

    // Legacy support: navigatorSetInitialFrame (deprecated)
    _disposables.add(
      this.slots.navigatorSetInitialFrame?.subscribe((frameIndex: number) => {
        const frames = this._frames;
        if (frameIndex >= 0 && frameIndex < frames.length) {
          this._displayedFrameIndex = frameIndex;
          // Don't navigate yet - fullscreenchange handler will do it
          this.requestUpdate();
        }
      })
    );
  }

  override firstUpdated() {
    const { _disposables } = this;

    this._bindHotKey();

    _disposables.add(
      this.slots.navigatorSettingUpdated.subscribe(({ fillScreen }) => {
        if (fillScreen !== undefined) {
          this._navigatorMode = fillScreen ? 'fill' : 'fit';
        }
      })
    );

    _disposables.addFromEvent(document, 'fullscreenchange', () => {
      if (document.fullscreenElement) {
        const frame = this._frames[this._displayedFrameIndex];
        const flight =
          this._entryCameraFlight && frame
            ? cinematicNavigateToFrame(this.gfx.viewport, frame, {
                mode: this._navigatorMode,
                style: 'smooth',
              })
            : Promise.resolve();

        void flight.finally(() => {
          this._entryCameraFlight = false;
          this._isAnimationEnabled = true;
        });
      } else {
        // Leaving browser fullscreen does not leave presentation mode. Keep
        // the same cinematic frame transition enabled in the inline canvas.
        this._isAnimationEnabled = true;
        if (
          this.edgelessTool.toolType === PresentTool &&
          this._fullScreenMode
        ) {
          this._fullScreenMode = false;
          if (this._exitPresentationAfterFullscreen) {
            this._exitPresentationAfterFullscreen = false;
            void this._restorePrePresentationViewport();
          }
        }
      }

      this.slots.fullScreenToggled.next();
    });

    this._navigatorMode =
      this.edgeless.std.get(EditPropsStore).getStorage('presentFillScreen') ===
      true
        ? 'fill'
        : 'fit';
  }

  override render() {
    const current = this._displayedFrameIndex;
    const frames = this._frames;
    const frame = frames[current];

    // In follower mode: simplified UI - only frame counter and exit button
    if (this._isFollowerMode) {
      return html`
        <style>
          :host {
            display: flex;
          }
        </style>
        <div class="edgeless-frame-navigator">
          ${this.dense
            ? nothing
            : html`<span
                style="color: ${cssVarV2('text/primary')}"
                class="edgeless-frame-navigator-title"
              >
                ${frame?.props.title ?? edgelessToolbarI18nT('toolbar.noFrame')}
              </span>`}

          <span class="edgeless-frame-navigator-count">
            ${frames.length === 0 ? 0 : current + 1} / ${frames.length}
          </span>
        </div>

        <div class="full-divider"></div>

        <button
          class="edgeless-frame-navigator-stop"
          @click=${this._exitPresentation}
          style="background: ${cssVarV2('status/warning')}"
        >
          ${StopAiIcon()}
        </button>
      `;
    }

    // Normal mode: full controls
    return html`
      <style>
        :host {
          display: flex;
        }
      </style>
      <edgeless-tool-icon-button
        .iconContainerPadding=${0}
        .tooltip=${edgelessToolbarI18nT('toolbar.previous')}
        .iconSize=${'24px'}
        @click=${() => this._requestPreviousFrame()}
      >
        ${StartPointArrowIcon()}
      </edgeless-tool-icon-button>

      <div class="edgeless-frame-navigator ${this.dense ? 'dense' : ''}">
        ${this.dense
          ? nothing
          : html`<span
              style="color: ${cssVarV2('text/primary')}"
              class="edgeless-frame-navigator-title"
              @click=${() => this._moveToFrame(this._displayedFrameIndex)}
            >
              ${frame?.props.title ?? edgelessToolbarI18nT('toolbar.noFrame')}
            </span>`}

        <span class="edgeless-frame-navigator-count">
          ${frames.length === 0 ? 0 : current + 1} / ${frames.length}
        </span>
      </div>

      <edgeless-tool-icon-button
        .tooltip=${edgelessToolbarI18nT('toolbar.next')}
        @click=${() => this._requestNextFrame()}
        .iconContainerPadding=${0}
        .iconSize=${'24px'}
      >
        ${EndPointArrowIcon()}
      </edgeless-tool-icon-button>

      <div class="full-divider"></div>

      <div class="config-buttons">
        <edgeless-tool-icon-button
          data-testid="presentation-toolbar-fullscreen"
          .tooltip=${document.fullscreenElement
            ? 'Exit Full Screen'
            : 'Enter Full Screen'}
          @click=${() =>
            document.fullscreenElement
              ? this._toggleFullScreen()
              : this._enterPresentation()}
          .iconContainerPadding=${0}
          .iconContainerWidth=${'24px'}
          .iconSize=${'24px'}
        >
          ${document.fullscreenElement ? ExpandCloseIcon() : ExpandFullIcon()}
        </edgeless-tool-icon-button>

        ${this.dense
          ? nothing
          : html`<edgeless-frame-order-button
              .popperShow=${this.frameMenuShow}
              .setPopperShow=${this.setFrameMenuShow}
              .edgeless=${this.edgeless}
            >
            </edgeless-frame-order-button>`}
      </div>

      <div class="full-divider"></div>

      <button
        class="edgeless-frame-navigator-stop"
        @click=${this._exitPresentation}
        style="background: ${cssVarV2('status/warning')}"
      >
        ${StopAiIcon()}
      </button>
    `;
  }

  // sl-2vda SOLID: Removed updated() lifecycle - no internal state to react to
  // Frame changes come from frameIndexChanged slot subscription

  private accessor _fullScreenMode = false;

  private _exitPresentationAfterFullscreen = false;

  @state()
  private accessor _navigatorMode: NavigatorMode = 'fit';

  @property({ attribute: false })
  accessor containerWidth = 1920;

  @property({ type: Boolean })
  accessor frameMenuShow = false;

  @property()
  accessor setFrameMenuShow: (show: boolean) => void = () => {};

  @property()
  accessor setSettingMenuShow: (show: boolean) => void = () => {};

  @property({ type: Boolean })
  accessor settingMenuShow = false;
}

function launchIntoFullscreen(element: Element) {
  if (element.requestFullscreen) {
    element.requestFullscreen().catch(console.error);
  } else if (
    'mozRequestFullScreen' in element &&
    element.mozRequestFullScreen instanceof Function
  ) {
    element.mozRequestFullScreen();
  } else if (
    'webkitRequestFullscreen' in element &&
    element.webkitRequestFullscreen instanceof Function
  ) {
    element.webkitRequestFullscreen();
  } else if (
    'msRequestFullscreen' in element &&
    element.msRequestFullscreen instanceof Function
  ) {
    element.msRequestFullscreen();
  }
}
